import { useEffect, useMemo, useRef, useState } from 'react';
import DeckGL from '@deck.gl/react';
import { EditableGeoJsonLayer } from '@nebula.gl/layers';
import {
  DrawPointMode,
  DrawLineStringMode,
  DrawPolygonMode,
  DrawRectangleMode,
  DrawCircleFromCenterMode,
  ModifyMode,
  ViewMode,
} from '@nebula.gl/edit-modes';
import { Map as MapLibre } from 'react-map-gl/maplibre';
import {
  Dot,
  Route,
  Pentagon,
  Square,
  Circle,
  Pencil,
  Trash2,
  X,
  Check,
} from 'lucide-react';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useUtmMapSettings } from '@/hooks/utm';
import type { DrawnFeature } from '@/utils/utm-geometry';
import { mergeGeometries, splitGeometry } from '@/utils/utm-geometry';

interface UtmDrawingModalProps {
  isOpen: boolean;
  initialGeometry?: any;
  onClose: () => void;
  onSave: (geometry: any) => void;
}

type DrawToolType = 'point' | 'line' | 'polygon' | 'rectangle' | 'circle' | null;

const INITIAL_VIEW_STATE = {
  longitude: 2.3522,
  latitude: 48.8566,
  zoom: 11,
  pitch: 0,
  bearing: 0,
};

const UtmDrawingModal = ({
  isOpen,
  initialGeometry,
  onClose,
  onSave,
}: UtmDrawingModalProps) => {
  const { data: mapSettings } = useUtmMapSettings();
  const [features, setFeatures] = useState<DrawnFeature[]>(() => {
    if (initialGeometry) {
      return splitGeometry(initialGeometry);
    }
    return [];
  });
  const [drawMode, setDrawMode] = useState<DrawToolType>(null);
  const [editingFeatureId, setEditingFeatureId] = useState<string | null>(null);
  const [selectedFeatureId, setSelectedFeatureId] = useState<string | null>(null);
  const [viewState, setViewState] = useState(INITIAL_VIEW_STATE);
  const hasAppliedDefaultMapSettings = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      hasAppliedDefaultMapSettings.current = false;
      return;
    }

    if (!mapSettings || hasAppliedDefaultMapSettings.current) {
      return;
    }

    setViewState((prev) => ({
      ...prev,
      longitude: mapSettings.longitude,
      latitude: mapSettings.latitude,
      zoom: mapSettings.zoom,
    }));
    hasAppliedDefaultMapSettings.current = true;
  }, [isOpen, mapSettings]);

  const geoJsonData = useMemo(
    () => ({
      type: 'FeatureCollection',
      features: features.map((f) => ({
        type: 'Feature',
        properties: { id: f.id },
        geometry: f.geometry,
      })),
    }),
    [features]
  );

  const getModeConfig = () => {
    if (editingFeatureId) {
      return new ModifyMode();
    }

    switch (drawMode) {
      case 'point':
        return new DrawPointMode();
      case 'line':
        return new DrawLineStringMode();
      case 'polygon':
        return new DrawPolygonMode();
      case 'rectangle':
        return new DrawRectangleMode();
      case 'circle':
        return new DrawCircleFromCenterMode();
      default:
        return new ViewMode();
    }
  };

  const handleEdit = ({ updatedData, editType }: any) => {
    if (editType === 'addFeature') {
      const newFeatureGeoJson = updatedData.features[updatedData.features.length - 1];
      const geometryType = newFeatureGeoJson.geometry.type;

      let displayType: DrawnFeature['type'] = geometryType;
      if (drawMode === 'circle') displayType = 'Circle';
      if (drawMode === 'rectangle') displayType = 'Rectangle';

      const newFeature: DrawnFeature = {
        id: crypto.randomUUID(),
        type: displayType,
        geometry: newFeatureGeoJson.geometry,
        label: `${displayType} #${features.length + 1}`,
        createdAt: Date.now(),
      };

      setFeatures([...features, newFeature]);
      setDrawMode(null);
    } else if (editType === 'updateFeature' && editingFeatureId) {
      const updatedFeatureGeoJson = updatedData.features.find(
        (f: any) => f.properties.id === editingFeatureId
      );

      if (updatedFeatureGeoJson) {
        setFeatures(
          features.map((f) =>
            f.id === editingFeatureId
              ? { ...f, geometry: updatedFeatureGeoJson.geometry }
              : f
          )
        );
      }
    }
  };

  const handleToolSelect = (tool: DrawToolType) => {
    setDrawMode(tool);
    setEditingFeatureId(null);
    setSelectedFeatureId(null);
  };

  const handleEditFeature = (featureId: string) => {
    const feature = features.find((f) => f.id === featureId);
    if (!feature) return;

    setEditingFeatureId(featureId);
    setSelectedFeatureId(featureId);
    setDrawMode(null);
  };

  const handleDeleteFeature = (featureId: string) => {
    setFeatures(features.filter((f) => f.id !== featureId));
    if (selectedFeatureId === featureId) {
      setSelectedFeatureId(null);
    }
    if (editingFeatureId === featureId) {
      setEditingFeatureId(null);
    }
  };

  const handleSelectFeature = (featureId: string) => {
    setSelectedFeatureId(featureId);
    setDrawMode(null);
    setEditingFeatureId(null);
  };

  const getInstructions = () => {
    if (editingFeatureId) {
      return 'Drag vertices to modify the feature. Click outside to finish editing.';
    }

    switch (drawMode) {
      case 'point':
        return 'Click to place a point';
      case 'line':
        return 'Click to add points, double-click to finish';
      case 'polygon':
        return 'Click to add vertices, double-click to close';
      case 'rectangle':
        return 'Click and drag to draw a rectangle';
      case 'circle':
        return 'Click center, then drag to set radius';
      default:
        return 'Select a drawing tool to start';
    }
  };

  const handleSave = () => {
    const mergedGeometry = mergeGeometries(features);
    onSave(mergedGeometry);
  };

  const handleCancel = () => {
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (editingFeatureId || drawMode) {
        setEditingFeatureId(null);
        setDrawMode(null);
      } else {
        handleCancel();
      }
    }
    if (e.key === 'Delete' && selectedFeatureId) {
      handleDeleteFeature(selectedFeatureId);
    }
  };

  const layer = useMemo(() => {
    const selectedIndex = selectedFeatureId
      ? features.findIndex((f) => f.id === selectedFeatureId)
      : -1;

    return new EditableGeoJsonLayer({
      id: 'utm-drawing-layer',
      data: geoJsonData,
      mode: getModeConfig(),
      selectedFeatureIndexes: selectedIndex >= 0 ? [selectedIndex] : [],
      onEdit: handleEdit,
      getFillColor: (f: any) => {
        const isSelected = f.properties.id === selectedFeatureId;
        return isSelected ? [59, 130, 246, 100] : [59, 130, 246, 60];
      },
      getLineColor: (f: any) => {
        const isSelected = f.properties.id === selectedFeatureId;
        return isSelected ? [37, 99, 235, 255] : [59, 130, 246, 200];
      },
      getLineWidth: 2,
      getTentativeLineColor: [37, 99, 235, 200],
      getTentativeFillColor: [59, 130, 246, 80],
      getTentativeLineDashArray: [4, 4],
    });
  }, [geoJsonData, drawMode, editingFeatureId, selectedFeatureId, features]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-card/95 backdrop-blur-sm"
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <div className="flex h-screen flex-col lg:flex-row">
        {/* Left Sidebar */}
        <div className="flex max-h-[45vh] w-full flex-col border-b border-border bg-card lg:max-h-none lg:w-80 lg:border-b-0 lg:border-r">
          {/* Instructions Panel */}
          <div className="p-4 bg-muted border-b border-border">
            <h3 className="text-sm font-medium text-foreground mb-2">Instructions</h3>
            <p className="text-xs text-foreground">{getInstructions()}</p>
          </div>

          {/* Drawing Tools */}
          <div className="border-b border-border p-4">
            <h3 className="text-sm font-medium text-foreground mb-3">Drawing Tools</h3>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-1">
              {(
                [
                  { tool: 'point', Icon: Dot, label: 'Point' },
                  { tool: 'line', Icon: Route, label: 'Line' },
                  { tool: 'polygon', Icon: Pentagon, label: 'Polygon' },
                  { tool: 'rectangle', Icon: Square, label: 'Rectangle' },
                  { tool: 'circle', Icon: Circle, label: 'Circle' },
                ] as const
              ).map(({ tool, Icon, label }) => (
                <button
                  key={tool}
                  type="button"
                  onClick={() => handleToolSelect(tool)}
                  className={`w-full flex items-center space-x-3 px-3 py-2 rounded transition-colors ${
                    drawMode === tool
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-foreground hover:bg-accent'
                  }`}
                >
                  <Icon size={18} />
                  <span className="text-sm">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Feature List */}
          <div className="overflow-y-auto p-4 lg:flex-1">
            <h3 className="text-sm font-medium text-foreground mb-3">
              Features ({features.length})
            </h3>
            {features.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p className="text-sm mb-1">No features drawn yet</p>
                <p className="text-xs">Select a tool from the toolbar to start drawing</p>
              </div>
            ) : (
              <div className="space-y-2">
                {features.map((feature) => {
                  const Icon =
                    feature.type === 'Point'
                      ? Dot
                      : feature.type === 'LineString'
                      ? Route
                      : feature.type === 'Polygon'
                      ? Pentagon
                      : feature.type === 'Rectangle'
                      ? Square
                      : Circle;

                  return (
                    <div
                      key={feature.id}
                      onClick={() => handleSelectFeature(feature.id)}
                      className={`flex items-center justify-between p-3 rounded cursor-pointer transition-colors ${
                        selectedFeatureId === feature.id
                          ? 'bg-muted border-l-4 border-primary'
                          : 'bg-muted/50 border-l-4 border-transparent hover:bg-accent'
                      }`}
                    >
                      <div className="flex items-center space-x-3 flex-1">
                        <Icon size={16} className="text-muted-foreground" />
                        <span className="text-sm text-foreground">{feature.label}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditFeature(feature.id);
                          }}
                          className="p-1.5 rounded hover:bg-accent text-muted-foreground hover:text-primary transition-colors"
                          title="Edit feature"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteFeature(feature.id);
                          }}
                          className="p-1.5 rounded hover:bg-accent text-muted-foreground hover:text-destructive transition-colors"
                          title="Delete feature"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Map Area */}
        <div className="relative min-h-0 flex-1">
          {/* Header Bar */}
          <div className="absolute left-0 right-0 top-0 z-10 flex items-center justify-between border-b border-border bg-card/90 px-3 py-3 backdrop-blur-sm sm:px-4 lg:px-6">
            <h2 className="text-base font-semibold text-foreground sm:text-lg lg:text-xl">Draw Geometry</h2>
            <div className="flex items-center gap-2 sm:space-x-3">
              <button
                type="button"
                onClick={handleCancel}
                className="flex items-center space-x-2 rounded bg-muted px-3 py-2 text-foreground transition-colors hover:bg-accent sm:px-4"
              >
                <X size={18} />
                <span className="hidden sm:inline">Cancel</span>
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={features.length === 0}
                className={`flex items-center space-x-2 rounded px-3 py-2 transition-colors sm:px-4 ${
                  features.length === 0
                    ? 'bg-muted text-muted-foreground cursor-not-allowed'
                    : 'bg-primary text-primary-foreground hover:bg-primary'
                }`}
              >
                <Check size={18} />
                <span className="hidden sm:inline">Done</span>
              </button>
            </div>
          </div>

          {/* DeckGL Map */}
          <div className="h-full pt-14 sm:pt-16">
            {/* @ts-ignore */}
            <DeckGL
              viewState={viewState}
              onViewStateChange={({ viewState: newViewState }: any) =>
                setViewState(newViewState)
              }
              controller={true}
              layers={[layer]}
              getCursor={() =>
                drawMode && !editingFeatureId ? 'crosshair' : 'grab'
              }
              style={{ width: '100%', height: '100%' }}
            >
              <MapLibre
                mapStyle="https://basemaps.cartocdn.com/gl/positron-gl-style/style.json"
                style={{ width: '100%', height: '100%' }}
              />
            </DeckGL>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UtmDrawingModal;
